
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { validateImage } from "@/utils/imageProcessing";
import ImageCropDialog from "@/components/common/ImageCropDialog";

const ProfileImageSection = () => {
  const { profile, updateProfile, refreshProfile } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);

  const handleProfileImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate the image file
    const errorMsg = validateImage(file, 5); // 5MB limit
    if (errorMsg) {
      toast({
        title: "Invalid image",
        description: errorMsg,
        variant: "destructive"
      });
      return;
    }
    
    // Create object URL for crop dialog
    const imageUrl = URL.createObjectURL(file);
    setSelectedImage(imageUrl);
    setShowCropDialog(true);
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    try {
      setIsUploading(true);
      setShowCropDialog(false);
      
      // Preview the cropped image
      setPreviewImage(URL.createObjectURL(croppedBlob));
      
      // Create a unique file name
      const fileName = `${profile?.id}-${Date.now()}.jpg`;
      const filePath = `${fileName}`;
      
      // Upload the cropped image to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('profiles')
        .upload(filePath, croppedBlob, {
          contentType: 'image/jpeg',
          upsert: true
        });
        
      if (uploadError) {
        throw uploadError;
      }
      
      // Get the public URL
      const { data } = supabase.storage
        .from('profiles')
        .getPublicUrl(filePath);
        
      // Update the profile with the new image URL
      if (data) {
        await updateProfile({
          profile_image_url: data.publicUrl
        });
        
        // Refresh the profile to get the updated data
        await refreshProfile();
        
        toast({
          title: "Profile image updated",
          description: "Your profile image has been updated successfully."
        });
      }
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message || "An error occurred while uploading your profile image.",
        variant: "destructive"
      });
      setPreviewImage(null);
    } finally {
      setIsUploading(false);
      // Clean up object URLs
      if (selectedImage) {
        URL.revokeObjectURL(selectedImage);
        setSelectedImage(null);
      }
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    }
  };

  return (
    <>
      <ImageCropDialog
        open={showCropDialog}
        imageSrc={selectedImage || ''}
        onClose={() => {
          setShowCropDialog(false);
          if (selectedImage) {
            URL.revokeObjectURL(selectedImage);
            setSelectedImage(null);
          }
        }}
        onCropComplete={handleCropComplete}
      />
      
      <Card>
        <CardContent className="pt-6">
        <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
          <div className="relative">
            {/* Fixed-size avatar with aspect ratio */}
            <div className="w-24 h-24">
              <AspectRatio ratio={1} className="bg-muted rounded-full overflow-hidden">
                <Avatar className="w-full h-full">
                  {previewImage ? (
                    <AvatarImage src={previewImage} alt="Profile preview" />
                  ) : (
                    <AvatarImage src={profile?.profile_image_url || ""} alt={profile?.full_name || "User"} />
                  )}
                  <AvatarFallback className="bg-godyspo-blue text-3xl">
                    {profile?.full_name ? profile.full_name.split(' ').map(n => n?.[0] || '').join('') : 'U'}
                  </AvatarFallback>
                </Avatar>
              </AspectRatio>
            </div>
            
            {/* Upload indicator */}
            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-full">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <h3 className="text-lg font-medium">{profile?.full_name || "User"}</h3>
            <p className="text-sm text-muted-foreground">{profile?.email || ""}</p>
            <div>
              <input
                id="profileImage"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleProfileImageSelect}
                disabled={isUploading}
              />
              <label
                htmlFor="profileImage"
                className="inline-flex items-center px-4 py-2 bg-godyspo-blue text-white text-sm font-medium rounded-md hover:bg-godyspo-blue/90 cursor-pointer disabled:opacity-50"
              >
                {isUploading ? "Uploading..." : "Upload Photo"}
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              For best results, upload a square image (jpg, png, gif). Will be cropped to a circle.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
    </>
  );
};

export default ProfileImageSection;
